import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ModalController, NavParams } from '@ionic/angular';

import { languageSupport, languageService } from '@app/modules/common/language';
import { WSConnection } from '@app/modules/sockets';
import { Subscription } from 'rxjs';

/**
 * "Cambio" (bill → coins) modal for a smart drawer (Prosegur).
 *
 * Orchestrates the deposit life-cycle with **quick, non-blocking** WS commands
 * so the connection is never frozen waiting for the customer:
 *
 *   START_DEPOSIT → poll CHECK_DEPOSIT (live amount) → FINISH_DEPOSIT →
 *   DISPENSE_COINS(total)
 *
 * All the cash logic (forcing coins by splitting the payout into sub-5€ chunks)
 * lives in `prosegur.rs`; this modal only drives the sequence and shows
 * progress. The parent (`ui-tab-cash`) connects and passes the connection +
 * WS target via `componentProps`, then disconnects on dismiss.
 */
@Component({
    selector: 'ui-modal-prosegur-change',
    templateUrl: './ui-prosegur-change.html',
    styleUrls: [ './ui-prosegur-change.scss' ]
})
export class UiProsegurChangeModal extends languageSupport implements OnInit, OnDestroy {
    /** Live connection to the drawer, supplied by the parent. */
    public _connection: WSConnection = null;
    /** WS target = drawer model (`PROSEGUR`). */
    public _target: string = 'PROSEGUR';

    public status: 'depositing' | 'dispensing' | 'success' | 'error' = 'depositing';
    public currentAmount: number = 0;   // euros (deposited so far)
    public totalDispensed: number = 0;   // euros (returned in coins)
    public errorMessage: string = null;

    private _opId: string = null;         // shared operationId for the deposit session
    private _depositOpen: boolean = false;
    private _cancelled: boolean = false;
    private _sub: Subscription = null;
    /** Moneda dispensable (céntimos) leída al abrir; -1 = desconocida. */
    private _coinFloor: number = -1;

    constructor(private lang: languageService, private change: ChangeDetectorRef, private modalCtrl: ModalController, private params: NavParams) {
        super(lang, change);
        this._connection = this.params.get('connection');
        this._target = this.params.get('target') || 'PROSEGUR';
    }

    get PhaseLabel(): string {
        switch (this.status) {
            case 'depositing':  return this.tr('@smartdrawer_change_insert');
            case 'dispensing':  return this.tr('@smartdrawer_change_dispensing');
            case 'success':     return this.tr('@smartdrawer_change_done');
            case 'error':       return this.errorMessage || this.tr('@smartdrawer_change_error');
            default:            return '';
        }
    }

    get IsRunning(): boolean {
        return this.status === 'depositing' || this.status === 'dispensing';
    }

    async ngOnInit() {
        if (!this._connection) {
            this._fail(this.tr('@cashguard_connection_failed'));
            return;
        }

        // Live dispense progress (raw frames from DISPENSE_COINS).
        this._sub = this._connection.OnMessageRecv.subscribe(data => this._onFrame(data));
        this._opId = 'chg' + Date.now();

        try {
            // 0) Cuánta MONEDA hay disponible para devolver (reciclador). Se lee
            //    ANTES de abrir el depósito: el depósito solo añade billetes al
            //    stacker, así que este suelo de monedas sigue siendo válido hasta
            //    el DISPENSE_COINS. Si el cajón no tiene monedas, no tiene sentido
            //    aceptar el billete: se quedaría atrapado al no poder dar cambio.
            //    Un -1 (lectura no disponible) NO bloquea: degradamos al flujo de
            //    siempre para no romper el cambio en backends sin CONTENTS.
            this._coinFloor = await this._readDispensableCoins();
            if (this._coinFloor === 0){
                this._fail(this.tr('@smartdrawer_change_nochange'));
                return;
            }

            // 1) Open the deposit so the customer can feed bills.
            if (!this._ok(await this._send('START_DEPOSIT', { operationId: this._opId }))){
                this._fail();
                return;
            }
            this._depositOpen = true;

            // 2) Poll the inserted amount until it settles (customer stopped).
            const total = await this._pollDeposit();
            if (this._cancelled){
                return;
            }
            if (total <= 0){
                await this._cancelDeposit();
                this._fail(this.tr('@smartdrawer_change_nocash'));
                return;
            }

            // 2b) ¿Puede el cajón devolver ese importe en monedas? Comprobamos
            //     ANTES de FINISH_DEPOSIT: mientras el depósito sigue abierto,
            //     CANCEL_DEPOSIT devuelve el billete al cliente. Una vez
            //     finalizado, el billete queda almacenado y ya no se puede
            //     retornar, que es justo el cuelgue que evitamos.
            if (this._coinFloor >= 0 && this._coinFloor < total){
                await this._cancelDeposit();
                this._depositOpen = false;
                this._fail(this.tr('@smartdrawer_change_nochange'));
                return;
            }

            // 3) Store the bills; use the authoritative FinishDeposit total.
            const fin = await this._send('FINISH_DEPOSIT', { operationId: this._opId });
            this._depositOpen = false;
            if (!this._ok(fin)){
                this._fail();
                return;
            }
            // Prefer FinishDeposit's total; fall back to the polled amount when
            // it reports 0/absent (some MDW builds omit it on finish).
            const finalTotal = this._num(this._body(fin), 'totalDeposited', 0) || total;

            // 4) Give it all back in coins. The backend dispenses in the
            //    background (slow machine mechanics) and streams progress +
            //    a final success/error frame — we complete on that, so the WS
            //    is never blocked for the whole payout.
            this.status = 'dispensing';
            this._dispensing = true;
            this.change.markForCheck();
            const accepted = await this._send('DISPENSE_COINS', { amount: finalTotal });
            if (!this._ok(accepted)){
                this._fail(this._body(accepted)?.['message'] || null);
                return;
            }
            await this._awaitDispenseDone();
        } catch (e) {
            this._fail();
        }
        this.change.markForCheck();
    }

    private _dispensing: boolean = false;
    private _dispensed: boolean = false;
    private _doneResolve: () => void = null;

    /** Resolves when the streamed dispense success/error frame arrives. */
    private _awaitDispenseDone(): Promise<void> {
        return new Promise<void>((resolve) => {
            // The final frame may arrive while DISPENSE_COINS is still being
            // acknowledged: we listen from before the Send, so complete right
            // away instead of waiting for a frame that already came.
            if (this._dispensed){
                resolve();
                return;
            }

            this._doneResolve = resolve;
            // Safety net so the modal never hangs if the final frame is lost.
            setTimeout(() => this._onDispenseTimeout(), 180000);
        });
    }

    /**
     * El frame final del dispensado no llegó en 180s. Si solo llamáramos a
     * `_resolveDone()` el estado quedaría en `'dispensing'` → spinner infinito,
     * botón de cerrar deshabilitado y "Aceptar" oculto (IsRunning sigue true).
     * Cerramos el flujo con un estado TERMINAL de aviso para desbloquear el
     * modal. No se reintenta el dispensado (Aceptar solo cierra), así que no hay
     * riesgo de doble dispensado; el operario verifica el cajón.
     */
    private _onDispenseTimeout() {
        if (this._dispensed){
            return;   // ya terminó por el frame de éxito/error
        }
        this.status = 'error';
        this.errorMessage = this.tr('@smartdrawer_change_dispense_timeout');
        this.change.markForCheck();
        this._resolveDone();
    }

    private _resolveDone() {
        this._dispensing = false;
        this._dispensed = true;
        if (this._doneResolve){
            const r = this._doneResolve;
            this._doneResolve = null;
            r();
        }
    }

    ngOnDestroy() {
        super.OnDestroy();
        this._cancelled = true;
        if (this._sub){
            this._sub.unsubscribe();
            this._sub = null;
        }
        // Safety net: never leave a deposit open if the modal dies mid-flow.
        if (this._depositOpen){
            this._depositOpen = false;
            this._cancelDeposit();
        }
    }

    OnClose() {
        this.modalCtrl.dismiss();
    }

    /*****************************/
    /* INTERNALS                 */
    /*****************************/

    /** Polls CHECK_DEPOSIT until the amount is non-zero and stable. */
    private async _pollDeposit(): Promise<number> {
        const _pollMs = 700;
        const _stableNeeded = 2;          // ~1.4 s unchanged after money is in
        const _maxWaitMs = 60000;         // give up after 60 s with nothing
        const _deadline = Date.now() + _maxWaitMs;
        let _last = -1;
        let _stable = 0;

        while (!this._cancelled){
            if (Date.now() > _deadline){
                return _last > 0 ? _last : 0;
            }
            const resp = await this._send('CHECK_DEPOSIT', { operationId: this._opId });
            const amount = this._num(this._body(resp), 'totalDeposited', 0);
            if (amount !== _last){
                _last = amount;
                _stable = 0;
                this.currentAmount = amount / 100;
                this.change.markForCheck();
            } else if (amount > 0){
                if (++_stable >= _stableNeeded){
                    return amount;
                }
            }
            await this._sleep(_pollMs);
        }
        return 0;
    }

    private async _cancelDeposit() {
        try { await this._send('CANCEL_DEPOSIT', { operationId: this._opId }); } catch (_) {}
    }

    /**
     * Suma la MONEDA dispensable (reciclador) en céntimos vía CONTENTS. El
     * DISPENSE_COINS del cambio paga solo en moneda (≤ 2€ = 200 cént.), así que
     * los billetes del reciclador no cuentan como cambio. Devuelve -1 si la
     * lectura no está disponible (backend antiguo / error) para NO bloquear el
     * flujo: en ese caso se conserva el comportamiento de siempre.
     */
    private async _readDispensableCoins(): Promise<number> {
        try {
            const resp = await this._send('CONTENTS', {});
            if (!this._ok(resp)){
                return -1;
            }
            const body = this._body(resp);
            // CONTENTS: { contents: { contents: [ { currency, denominations:
            //   [ { denomination, deposited, recyclable } ] } ] } }
            const outer = body && body['contents'];
            const list = (outer && outer['contents']) || (Array.isArray(outer) ? outer : null);
            if (!Array.isArray(list)){
                return -1;
            }
            let cents = 0;
            let sawDenoms = false;
            for (const cur of list){
                const denoms = cur && cur['denominations'];
                if (!Array.isArray(denoms)){
                    continue;
                }
                for (const d of denoms){
                    const denom = this._num(d, 'denomination', 0);
                    const pieces = this._num(d, 'recyclable', 0);
                    if (denom <= 0){
                        continue;
                    }
                    sawDenoms = true;
                    // Solo moneda: la denominación de billete más pequeña en EUR
                    // es 5€ (500 cént.); todo lo ≤ 2€ (200) es moneda.
                    if (denom <= 200 && pieces > 0){
                        cents += denom * pieces;
                    }
                }
            }
            return sawDenoms ? cents : -1;
        } catch (_) {
            return -1;
        }
    }

    private _send(command: string, extra?: any): Promise<any> {
        return this._connection.Send(this._target, Object.assign({ command }, extra || {}));
    }

    private _ok(resp: any): boolean {
        const body = this._body(resp);
        return !!(resp && resp.exec === 'SUCCESS' && (!body || body['status'] !== 'error'));
    }

    private _body(resp: any): any {
        let data = resp && resp['data'];
        if (typeof data === 'string'){
            try { data = JSON.parse(data); } catch (_) { return null; }
        }
        return (data && typeof data === 'object') ? data : null;
    }

    private _num(obj: any, key: string, def: number): number {
        const v = obj && obj[key];
        return (typeof v === 'number') ? v : (v !== undefined && v !== null && !isNaN(Number(v)) ? Number(v) : def);
    }

    private _sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private _fail(message?: string) {
        this.status = 'error';
        this.errorMessage = message || this.tr('@smartdrawer_change_error');
        this.change.markForCheck();
    }

    /**
     * Streamed dispense frames. Live progress updates the returned amount;
     * the final success/error frame (only honoured while dispensing) ends the
     * flow — this is how the modal completes, not the blocking Send response.
     */
    private _onFrame(msg: any) {
        const body = this._body(msg);
        if (!body || !body['status']){
            return;
        }
        if (body['status'] === 'progress'){
            if (body['phase'] === 'DISPENSING_COINS' && body['currentAmount'] !== undefined){
                this.totalDispensed = (body['currentAmount'] || 0) / 100;
                this.change.markForCheck();
            }
            return;
        }
        if (!this._dispensing){
            return;   // ignore success/error frames outside the dispense phase
        }
        if (body['status'] === 'success'){
            this.status = 'success';
            if (body['amountRefund'] !== undefined){
                this.totalDispensed = (body['amountRefund'] || 0) / 100;
            }
            this.change.markForCheck();
            this._resolveDone();
        } else if (body['status'] === 'error'){
            this._fail(body['message']);
            this._resolveDone();
        }
    }
}
